import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError, setAccessToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LEGAL_LINKS } from "../lib/legal";
import {
    DownloadSimple, Trash, ShieldCheck, Warning, FloppyDisk, EnvelopeSimple, Receipt, ArrowSquareOut,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import InvestorProfileForm from "../components/InvestorProfileForm";
import { useTheme } from "../context/ThemeContext";
import { Moon, SunDim, Desktop } from "@phosphor-icons/react";

function ThemeSwitcherCard() {
    const { theme, setTheme } = useTheme();
    const OPTIONS = [
        { key: "dark", label: "Dark", desc: "Premium notturno", icon: Moon },
        { key: "light", label: "Light", desc: "Classico professionale", icon: SunDim },
        { key: "system", label: "System", desc: "Segue il dispositivo", icon: Desktop },
    ];
    return (
        <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="account-theme">
            <h2 className="text-base font-bold mb-1">Tema interfaccia</h2>
            <p className="text-sm text-gray-500 mb-4">
                La preferenza è personale e viene salvata sul tuo profilo.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md" data-testid="theme-switcher">
                {OPTIONS.map((o) => {
                    const Icon = o.icon;
                    const active = theme === o.key;
                    return (
                        <button
                            key={o.key}
                            data-testid={`theme-option-${o.key}`}
                            onClick={() => setTheme(o.key)}
                            aria-pressed={active}
                            className={`p-3 border text-left transition-colors rounded-lg ${active ? "border-purple-300 bg-purple-50 ring-1 ring-purple-300" : "border-gray-200 hover:border-gray-300"}`}
                        >
                            <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-purple-600" : "text-gray-500"} />
                            <div className="text-sm font-bold mt-1.5">{o.label}</div>
                            <div className="text-[11px] text-gray-500">{o.desc}</div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function Account() {
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [consents, setConsents] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [profile, setProfile] = useState({ full_name: "", organization_name: "" });
    const [savingProfile, setSavingProfile] = useState(false);
    const [delOpen, setDelOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [invoices, setInvoices] = useState(null);

    useEffect(() => {
        api.get("/payments/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch(() => setInvoices([]));
    }, []);

    useEffect(() => {
        api.get("/account/consents").then(({ data }) => setConsents(data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (user) setProfile({ full_name: user.full_name || "", organization_name: user.organization_name || "" });
    }, [user]);

    const saveProfile = async () => {
        setSavingProfile(true);
        try {
            await api.patch("/account/profile", profile);
            await refresh();
            toast.success("Dati aggiornati");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSavingProfile(false);
        }
    };

    const exportData = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/account/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `asta-intelligence-dati-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Esportazione completata");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setExporting(false);
        }
    };

    const deleteAccount = async () => {
        if (!password) { toast.error("Inserisci la password"); return; }
        if (confirmText.trim() !== "ELIMINA") { toast.error('Digita ELIMINA per confermare'); return; }
        setDeleting(true);
        try {
            await api.delete("/account", { data: { password, confirm: true, confirmation_text: confirmText } });
            setAccessToken(null);
            toast.success("Account e dati cancellati");
            await logout();
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setDeleting(false);
        }
    };

    const canDelete = password && confirmText.trim() === "ELIMINA";

    return (
        <Layout>
            <div className="max-w-3xl" data-testid="account-page">
                <div className="overline mb-2">Privacy e Dati</div>
                <h1 className="font-display-black text-4xl mb-2 leading-tight">Privacy &amp; i tuoi dati</h1>
                <p className="text-sm text-gray-600 mb-8">
                    Gestisci i tuoi dati personali ed esercita i tuoi diritti ai sensi del GDPR.
                </p>

                <InvestorProfileForm />

                {/* Modifica dati personali */}
                <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="account-profile">
                    <h2 className="text-base font-bold mb-1">Modifica dati personali</h2>
                    <p className="text-sm text-gray-500 mb-4">Diritto di rettifica (art. 16 GDPR).</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="overline block mb-2">Nome e cognome</label>
                            <Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="profile-name" className="h-11" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Organizzazione</label>
                            <Input value={profile.organization_name} onChange={(e) => setProfile({ ...profile, organization_name: e.target.value })} data-testid="profile-org" className="h-11" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                        <div><div className="overline mb-1">Email</div>{user?.email || "—"}</div>
                        <div><div className="overline mb-1">Ruolo</div>{user?.role || "—"}</div>
                    </div>
                    <button
                        onClick={saveProfile}
                        disabled={savingProfile}
                        data-testid="profile-save"
                        className="mt-4 h-10 px-4 inline-flex items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                        <FloppyDisk size={16} weight="bold" /> {savingProfile ? "Salvataggio…" : "Salva modifiche"}
                    </button>
                </div>

                {/* Tema interfaccia */}
                <ThemeSwitcherCard />

                {/* Consensi */}
                <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="account-consents">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck size={18} weight="fill" className="text-emerald-700" />
                        <h2 className="text-base font-bold">Storico consensi</h2>
                    </div>
                    {consents.length === 0 ? (
                        <p className="text-sm text-gray-500">Nessun consenso registrato.</p>
                    ) : (
                        <ul className="space-y-2 text-sm text-gray-700">
                            {consents.map((c, i) => (
                                <li key={i} className="flex flex-wrap gap-x-4 gap-y-1 border-b border-gray-100 pb-2 last:border-0">
                                    <span>Privacy v{c.privacy_version} · Termini v{c.terms_version}</span>
                                    <span className="text-gray-400">{c.accepted_at?.slice(0, 19).replace("T", " ")}</span>
                                    <span className="text-gray-400">({c.context})</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <nav className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs">
                        {LEGAL_LINKS.map((l) => (
                            <Link key={l.to} to={l.to} className="font-semibold text-gray-500 hover:text-black underline underline-offset-4">{l.label}</Link>
                        ))}
                    </nav>
                </div>

                {/* Fatture */}
                <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="account-invoices">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Receipt size={18} weight="fill" className="text-emerald-700" />
                            <h2 className="text-base font-bold">Fatture e ricevute</h2>
                        </div>
                        <Link to="/piano" className="text-xs font-semibold text-gray-500 hover:text-black underline underline-offset-4" data-testid="invoices-manage-plan-link">
                            Gestisci piano
                        </Link>
                    </div>
                    {invoices === null ? (
                        <p className="text-sm text-gray-500">Caricamento fatture…</p>
                    ) : invoices.length === 0 ? (
                        <p className="text-sm text-gray-500" data-testid="invoices-empty">
                            Nessuna fattura: le fatture Stripe compariranno qui dopo il primo pagamento.
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100" data-testid="invoices-list">
                            {invoices.map((inv) => (
                                <li key={inv.id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`invoice-row-${inv.id}`}>
                                    <span className="font-mono-tight text-gray-700 w-24 shrink-0">{inv.date?.split("-").reverse().join("/")}</span>
                                    <span className="font-semibold w-24 shrink-0">
                                        €{((inv.amount || 0) / 100).toFixed(2).replace(".", ",")}
                                    </span>
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${inv.status === "paid" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                                        {inv.status === "paid" ? "Pagata" : inv.status}
                                    </span>
                                    <span className="text-gray-500 flex-1 min-w-[140px] truncate">{inv.number || inv.description || ""}</span>
                                    <span className="flex items-center gap-3 ml-auto">
                                        {inv.pdf_url && (
                                            <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" data-testid={`invoice-pdf-${inv.id}`}
                                               className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-black">
                                                <DownloadSimple size={14} /> PDF
                                            </a>
                                        )}
                                        {inv.hosted_url && (
                                            <a href={inv.hosted_url} target="_blank" rel="noopener noreferrer" data-testid={`invoice-view-${inv.id}`}
                                               className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-black">
                                                <ArrowSquareOut size={14} /> Apri
                                            </a>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Export + Contatta titolare */}
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div className="border border-gray-200 bg-white p-5 flex flex-col justify-between gap-4">
                        <div>
                            <h2 className="text-base font-bold mb-1">Scarica i miei dati</h2>
                            <p className="text-sm text-gray-500">Copia JSON (portabilità, art. 20 GDPR).</p>
                        </div>
                        <button
                            onClick={exportData}
                            disabled={exporting}
                            data-testid="export-data-button"
                            className="h-10 px-4 inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-black text-sm font-semibold disabled:opacity-50 transition-colors"
                        >
                            <DownloadSimple size={16} weight="bold" /> {exporting ? "Esporto…" : "Esporta"}
                        </button>
                    </div>
                    <div className="border border-gray-200 bg-white p-5 flex flex-col justify-between gap-4">
                        <div>
                            <h2 className="text-base font-bold mb-1">Contatta il titolare</h2>
                            <p className="text-sm text-gray-500">Richieste privacy o assistenza.</p>
                        </div>
                        <Link
                            to="/contatti"
                            data-testid="account-contact-link"
                            className="h-10 px-4 inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-black text-sm font-semibold transition-colors"
                        >
                            <EnvelopeSimple size={16} weight="bold" /> Contatti
                        </Link>
                    </div>
                </div>

                {/* Danger zone */}
                <div className="border border-red-200 bg-red-500/[0.07] p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Warning size={18} weight="fill" className="text-red-600" />
                        <h2 className="text-base font-bold text-red-700">Cancella account</h2>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                        Elimina definitivamente account, organizzazione e tutti i dati associati. Operazione irreversibile (art. 17 GDPR).
                    </p>
                    <button
                        onClick={() => setDelOpen(true)}
                        data-testid="delete-account-open-button"
                        className="h-10 px-4 inline-flex items-center gap-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
                    >
                        <Trash size={16} weight="bold" /> Cancella il mio account
                    </button>
                </div>
            </div>

            <Dialog open={delOpen} onOpenChange={setDelOpen}>
                <DialogContent data-testid="delete-account-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display-black text-2xl">Confermi la cancellazione?</DialogTitle>
                        <DialogDescription className="text-sm text-gray-500">
                            Operazione irreversibile. Inserisci la password e digita <strong>ELIMINA</strong> per confermare.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="La tua password"
                            data-testid="delete-account-password"
                            className="h-11"
                        />
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="Digita ELIMINA"
                            data-testid="delete-account-confirm-text"
                            className="h-11"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={() => setDelOpen(false)} className="h-10 px-4 text-sm font-semibold text-gray-500 hover:text-black transition-colors">
                            Annulla
                        </button>
                        <button
                            onClick={deleteAccount}
                            disabled={deleting || !canDelete}
                            data-testid="delete-account-confirm-button"
                            className="h-10 px-5 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 transition-colors"
                        >
                            {deleting ? "Cancellazione…" : "Cancella definitivamente"}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
